import { Building2, ChevronDown, Crown, Import, Settings, Shield, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { useRolePermissions } from "../providers/commons/useRolePermissions";
import { ImportPage } from "../misc/ImportPage";
import { CompanySettingsPage } from "../settings/CompanySettingsPage";
import { RolesPage } from "../settings/RolesPage";
import { VipTiersPage } from "../settings/VipTiersPage";

// Map path prefixes → group key
const GROUP_PATHS: Record<string, string> = {
  "/inquiries": "sales",
  "/quotations": "sales",
  "/orders": "sales",
  "/members": "sales",
  "/logistics": "supply",
  "/products": "supply",
  "/suppliers": "supply",
  "/purchase-orders": "supply",
  "/quality-inspections": "supply",
  "/returns": "supply",
  "/ar": "finance",
  "/expenses": "finance",
  "/complaints": "finance",
  "/reports/sales": "finance",
  "/workflow": "finance",
  "/audit-log": "finance",
  "/contacts": "crm",
  "/companies": "crm",
  "/deals": "crm",
};

function useCurrentGroup(pathname: string): string {
  if (matchPath("/", pathname)) return "/";
  for (const [prefix, group] of Object.entries(GROUP_PATHS)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return group;
  }
  return "";
}

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const activeGroup = useCurrentGroup(location.pathname);
  const { can } = useRolePermissions();

  // Filter a list of nav items, keeping only those the user can read
  const filter = (
    items: { label: string; to: string; resource?: string }[],
  ) =>
    items.filter((item) =>
      !item.resource || can(item.resource, "read"),
    );

  return (
    <header className="bg-secondary">
      <div className="px-4">
        <div className="flex justify-between items-center flex-1">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-secondary-foreground no-underline shrink-0"
          >
            <img className="[.light_&]:hidden h-6" src={darkModeLogo} alt={title} />
            <img className="[.dark_&]:hidden h-6" src={lightModeLogo} alt={title} />
            <h1 className="text-xl font-semibold">{title}</h1>
          </Link>

          {/* Nav */}
          <nav className="flex items-stretch overflow-x-auto">
            <NavigationTab label="仪表盘" to="/" isActive={activeGroup === "/"} />

            {/* CRM 客户管理 */}
            <NavDropdown label="客户" isActive={activeGroup === "crm"} items={filter([
              { label: "联系人", to: "/contacts", resource: "contacts" },
              { label: "公司", to: "/companies", resource: "companies" },
              { label: "交易", to: "/deals", resource: "deals" },
            ])} />

            {/* 销售流程 */}
            <NavDropdown label="销售" isActive={activeGroup === "sales"} items={filter([
              { label: "询盘", to: "/inquiries", resource: "inquiries" },
              { label: "报价单", to: "/quotations", resource: "quotations" },
              { label: "订单", to: "/orders", resource: "orders" },
              { label: "会员积分", to: "/members", resource: "members" },
            ])} />

            {/* 供应链 */}
            <NavDropdown label="供应链" isActive={activeGroup === "supply"} items={filter([
              { label: "产品目录", to: "/products", resource: "products" },
              { label: "供应商", to: "/suppliers", resource: "suppliers" },
              { label: "采购单", to: "/purchase-orders", resource: "purchase_orders" },
              { label: "质检记录", to: "/quality-inspections", resource: "quality_inspections" },
              { label: "物流跟踪", to: "/logistics", resource: "logistics" },
              { label: "退货换货", to: "/returns", resource: "returns" },
            ])} />

            {/* 财务 */}
            <NavDropdown label="财务" isActive={activeGroup === "finance"} items={filter([
              { label: "应收账款", to: "/ar", resource: "ar" },
              { label: "费用报销", to: "/expenses", resource: "expenses" },
              { label: "投诉索赔", to: "/complaints", resource: "complaints" },
              { label: "销售报表", to: "/reports/sales", resource: "reports" },
              { label: "工作流", to: "/workflow", resource: "workflow" },
              { label: "审计日志", to: "/audit-log", resource: "audit_log" },
            ])} />
          </nav>

          {/* Right actions */}
          <div className="flex items-center shrink-0">
            <ThemeModeToggle />
            <RefreshButton />
            <UserMenu>
              <ProfileMenu />
              <CanAccess resource="sales" action="list">
                <UsersMenu />
              </CanAccess>
              <CanAccess resource="configuration" action="edit">
                <SettingsMenu />
                <CompanySettingsMenu />
                <VipTiersMenu />
                <RolesMenu />
              </CanAccess>
              <ImportFromJsonMenuItem />
            </UserMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

// ── Plain tab ────────────────────────────────────────────────────────────────
const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
      isActive
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`}
  >
    {label}
  </Link>
);

// ── Dropdown group tab ───────────────────────────────────────────────────────
const NavDropdown = ({
  label,
  isActive,
  items,
}: {
  label: string;
  isActive: boolean;
  items: { label: string; to: string }[];
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className={`flex items-center gap-0.5 px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 outline-none ${
          isActive
            ? "text-secondary-foreground border-secondary-foreground"
            : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
        }`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="min-w-[140px]">
      {items.map((item) => (
        <DropdownMenuItem key={item.to} asChild>
          <Link to={item.to} className="w-full">
            {item.label}
          </Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// ── User menu items ──────────────────────────────────────────────────────────
const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<UsersMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<ProfileMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const CompanySettingsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<CompanySettingsMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={CompanySettingsPage.path} className="flex items-center gap-2">
        <Building2 />
        Company & Trade
      </Link>
    </DropdownMenuItem>
  );
};

const VipTiersMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<VipTiersMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={VipTiersPage.path} className="flex items-center gap-2">
        <Crown />
        VIP 等级积分设置
      </Link>
    </DropdownMenuItem>
  );
};

const RolesMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<RolesMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={RolesPage.path} className="flex items-center gap-2">
        <Shield />
        角色权限
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
